import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Trash2, Plus, CheckCircle2, AlertCircle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export default function AdminImpactStats() {
    const [stats, setStats] = useState([]);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        const { data } = await api.get("/admin/impact-stats");
        setStats((data.stats || []).map((s) => ({ prefix: "", suffix: "", verified: false, ...s })));
    }, []);
    useEffect(() => { load(); }, [load]);

    function update(i, field, value) {
        setStats((s) => s.map((row, idx) => (idx === i ? { ...row, [field]: value } : row)));
    }
    function add() {
        setStats((s) => [...s, { key: `new_${Date.now()}`, label: "New metric", value: 0, prefix: "", suffix: "", order: (s.length + 1), verified: false }]);
    }
    async function save(row) {
        setBusy(true);
        try {
            await api.post("/admin/impact-stats", row);
            toast.success(row.verified ? "Saved · Published" : "Saved · Draft (mark Verified to publish)");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    }
    async function remove(key) {
        // eslint-disable-next-line no-alert
        if (!window.confirm("Delete this metric?")) return;
        try { await api.delete(`/admin/impact-stats/${key}`); toast.success("Deleted"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    }

    return (
        <div data-testid="admin-impact-stats">
            <div className="gsf-eyebrow">Impact</div>
            <h1 className="mt-2 font-serif text-3xl text-[color:var(--gsf-green-700)] font-bold">Impact statistics</h1>
            <p className="mt-3 text-sm text-gray-600 max-w-2xl">
                Displayed on the homepage and Impact page. Only metrics marked <strong>Verified</strong> are shown publicly.
                Use <em>Prefix</em> for currency symbols (for example <code>USD&nbsp;</code>) and <em>Suffix</em> for units
                (for example <code>+</code>).
            </p>

            <div className="mt-8 gsf-card overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-[#f8faf7] text-left">
                        <tr>
                            <th className="p-3">Key</th>
                            <th className="p-3">Label</th>
                            <th className="p-3 w-20">Prefix</th>
                            <th className="p-3 w-24">Value</th>
                            <th className="p-3 w-20">Suffix</th>
                            <th className="p-3 w-16">Order</th>
                            <th className="p-3 w-24">Verified</th>
                            <th className="p-3 w-24">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {stats.map((s, i) => (
                            <tr key={s.key} className="border-t border-gray-100" data-testid={`stat-row-${s.key}`}>
                                <td className="p-3"><input value={s.key} onChange={(e) => update(i, "key", e.target.value)} data-testid={`stat-key-${i}`} className="w-full rounded border border-gray-200 px-2 py-1.5" /></td>
                                <td className="p-3"><input value={s.label} onChange={(e) => update(i, "label", e.target.value)} data-testid={`stat-label-${i}`} className="w-full rounded border border-gray-200 px-2 py-1.5" /></td>
                                <td className="p-3"><input value={s.prefix || ""} placeholder="e.g. USD " onChange={(e) => update(i, "prefix", e.target.value)} data-testid={`stat-prefix-${i}`} className="w-full rounded border border-gray-200 px-2 py-1.5" /></td>
                                <td className="p-3"><input type="number" value={s.value} onChange={(e) => update(i, "value", parseInt(e.target.value) || 0)} data-testid={`stat-value-${i}`} className="w-full rounded border border-gray-200 px-2 py-1.5" /></td>
                                <td className="p-3"><input value={s.suffix || ""} onChange={(e) => update(i, "suffix", e.target.value)} data-testid={`stat-suffix-${i}`} className="w-full rounded border border-gray-200 px-2 py-1.5" /></td>
                                <td className="p-3"><input type="number" value={s.order} onChange={(e) => update(i, "order", parseInt(e.target.value) || 0)} data-testid={`stat-order-${i}`} className="w-16 rounded border border-gray-200 px-2 py-1.5" /></td>
                                <td className="p-3">
                                    <label className="inline-flex items-center gap-2 cursor-pointer" data-testid={`stat-verified-toggle-${i}`}>
                                        <input type="checkbox" checked={!!s.verified} onChange={(e) => update(i, "verified", e.target.checked)} data-testid={`stat-verified-${i}`} />
                                        {s.verified ? (
                                            <span className="inline-flex items-center gap-1 text-[color:var(--gsf-green-700)] text-xs font-medium"><CheckCircle2 size={14} /> Published</span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 text-amber-600 text-xs font-medium"><AlertCircle size={14} /> Draft</span>
                                        )}
                                    </label>
                                </td>
                                <td className="p-3">
                                    <div className="flex gap-2">
                                        <button onClick={() => save(s)} disabled={busy} className="text-[color:var(--gsf-green-700)] hover:bg-[color:var(--gsf-soft-green)] rounded-lg p-1.5" data-testid={`stat-save-${i}`}><Save size={16} /></button>
                                        <button onClick={() => remove(s.key)} className="text-red-600 hover:bg-red-50 rounded-lg p-1.5" data-testid={`stat-delete-${i}`}><Trash2 size={16} /></button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <button onClick={add} className="mt-6 gsf-btn-outline text-sm" data-testid="stat-add"><Plus size={14} /> Add metric</button>
        </div>
    );
}
